import { Badge, Button, Tooltip } from "@jhool-io/fe-components";
import { displayNameInRightFormat } from "../../../utils/helpers";
import UserIcon from "../../../icons/User";
import {
    IBatchRemitAssignee,
    IClaimAdjustments,
    IInsurancePayments,
    IRemit,
    RemitAdjustmentRow,
} from "../types/remits.types";

const addCodeAmount = (
    totals: Map<string, number>,
    code: string,
    amount: number | null
) => {
    const normalizedCode = code.toLowerCase();
    totals.set(
        normalizedCode,
        (totals.get(normalizedCode) ?? 0) + Number(amount ?? 0)
    );
};

export const getAdjustmentCodeTotals = (
    adjustments: IClaimAdjustments[] | null
) => {
    const totals = new Map<string, number>();

    adjustments?.forEach((adjustment) => {
        if (adjustment.reason_code) {
            addCodeAmount(
                totals,
                `${adjustment.group_code}${adjustment.reason_code}`,
                adjustment.amount
            );
        }
        if (adjustment.reason_code_2) {
            addCodeAmount(
                totals,
                `${adjustment.group_code}${adjustment.reason_code_2}`,
                adjustment.amount_2
            );
        }
    });

    return totals;
};

export const getPaymentRemarkCodeTotals = (payments: IInsurancePayments[]) => {
    const totals = new Map<string, number>();

    payments.forEach((payment) => {
        Object.entries(payment.remark_codes_payment ?? {}).forEach(
            ([code, amount]) => addCodeAmount(totals, code, amount)
        );
    });

    return totals;
};

export const getBatchRemitAssigneeToDisplay = (
    assignees: IBatchRemitAssignee[] | null,
    userId: string
) => {
    const activeAssignees =
        assignees?.filter((a) => a.status !== "unlocked") ?? null;

    const assigneeForUser =
        activeAssignees?.find((a) => a.user?.id === userId) ?? null;

    const isAssignedToMe = Boolean(assigneeForUser);

    const usersNameToDisplay = (() => {
        if (activeAssignees && activeAssignees.length > 0) {
            if (activeAssignees.length > 1) {
                return (
                    <Tooltip
                        content={`Assigned to: ${
                            assigneeForUser ? "You," : ""
                        } ${activeAssignees
                            .filter((a) => a.user.id !== userId)
                            .map(
                                (a) =>
                                    `${displayNameInRightFormat(
                                        {
                                            firstName: a.user.first_name,
                                            lastName: a.user.last_name,
                                        },
                                        false
                                    )}`
                            )
                            .join(" , ")}`}
                    >
                        <Button size="auto" variant="normal">
                            <Badge
                                bgColor="var(--color-info-bg)"
                                textColor="var(--color-info-text)"
                                type="custom"
                                content={
                                    <span className="flex items-center gap-x-1 text-[12px]">
                                        <UserIcon className="size-[12px] stroke-[--color-info-text]" />
                                        {`${isAssignedToMe ? `You and ` : ""} ${
                                            isAssignedToMe
                                                ? activeAssignees.length - 1
                                                : activeAssignees.length
                                        } user${
                                            isAssignedToMe &&
                                            activeAssignees.length - 1 === 1
                                                ? ""
                                                : "s"
                                        }`}
                                    </span>
                                }
                            />
                        </Button>
                    </Tooltip>
                );
            }

            if (activeAssignees.length === 1 && !isAssignedToMe) {
                return (
                    <Badge
                        bgColor="var(--color-info-bg)"
                        textColor="var(--color-info-text)"
                        type="custom"
                        content={
                            <span className="flex items-center gap-x-1">
                                <UserIcon className="size-[12px] stroke-[--color-info-text]" />
                                {displayNameInRightFormat(
                                    {
                                        firstName:
                                            activeAssignees[0].user.first_name,
                                        lastName:
                                            activeAssignees[0].user.last_name,
                                    },
                                    false
                                )}
                            </span>
                        }
                    />
                );
            }

            if (activeAssignees.length === 1 && isAssignedToMe) {
                return (
                    <Badge
                        bgColor="var(--color-info-bg)"
                        textColor="var(--color-info-text)"
                        type="custom"
                        content={
                            <span className="flex items-center gap-x-1 text-[12px]">
                                <svg
                                    viewBox="0 0 24 24"
                                    width="24"
                                    height="24"
                                    stroke="var(--color-info-text)"
                                    strokeWidth="2"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="size-[12px] stroke-[--color-info-text]"
                                >
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                </svg>
                                You
                            </span>
                        }
                    />
                );
            }
        }

        return "--";
    })();

    return {
        assigneeForUser,
        isAssignedToMe,
        usersNameToDisplay,
    };
};

export const getRemitsListNavigateUrl = () => {
    const remitsListFiltersFromLS = localStorage.getItem("remitslist_filters");

    if (remitsListFiltersFromLS) {
        return `/remits?${remitsListFiltersFromLS}`;
    }
    return `/remits`;
};

const hasAdjustmentContent = (
    code?: string,
    description?: string,
    amount?: number
) => {
    return (
        Boolean(code?.trim()) ||
        Boolean(description?.trim()) ||
        (amount ?? 0) !== 0
    );
};

export const buildRemitAdjustmentRows = (
    adjustments: IClaimAdjustments[],
    cpt: string
): RemitAdjustmentRow[] => {
    const rows: RemitAdjustmentRow[] = [];

    adjustments.forEach((adjustment, adjustmentIndex) => {
        const groupCode = adjustment.group_code || "";

        if (
            hasAdjustmentContent(
                adjustment.reason_code,
                adjustment.reason_description,
                adjustment.amount
            )
        ) {
            rows.push({
                id: `${cpt}-${adjustmentIndex}-1`,
                cpt,
                code: adjustment.reason_code || "",
                groupCode,
                description: adjustment.reason_description || "",
                amount: adjustment.amount || 0,
            });
        }

        if (
            hasAdjustmentContent(
                adjustment.reason_code_2,
                adjustment.reason_description_2,
                adjustment.amount_2
            )
        ) {
            rows.push({
                id: `${cpt}-${adjustmentIndex}-2`,
                cpt,
                code: adjustment.reason_code_2 || "",
                groupCode,
                description: adjustment.reason_description_2 || "",
                amount: adjustment.amount_2 || 0,
            });
        }
    });

    return rows;
};

export const getRemitOutstanding = (remit: IRemit): number => {
    if (remit.insurance_charged && remit.insurance_charged > 0) {
        return (
            remit.insurance_charged -
            (remit.pr100_collected ?? 0) -
            (remit.insurance_paid +
                remit.insurance_writeoff +
                remit.patient_paid +
                remit.patient_writeoff)
        );
    }
    return (
        (remit.patient_charged ?? 0) -
        (remit.patient_paid + remit.patient_writeoff)
    );
};

export const buildBatchRemitAdjustmentRows = (
    remits: IRemit[]
): RemitAdjustmentRow[] =>
    remits.flatMap((remit) =>
        buildRemitAdjustmentRows(
            remit.claim_adjustments || [],
            remit.remittance_cpt_code || remit.cpt_code
        )
    );

export const groupRemitsIntoTabs = (remits: IRemit[]): IRemit[][] => {
    const groups: Record<string, IRemit[]> = {};
    remits.forEach((remit) => {
        const dateOnly = remit.remit_payment_date?.split("T")[0] ?? "";
        const key = `${remit.remittance_batch_id}::${remit.claim_id}::${dateOnly}`;
        const existing = groups[key];
        if (existing) existing.push(remit);
        else groups[key] = [remit];
    });

    const tabs: IRemit[][] = [];
    Object.values(groups).forEach((groupRemits) => {
        const cptCodes = groupRemits.map((r) => r.remittance_cpt_code);
        const hasNull = cptCodes.some((c) => !c);
        const allUnique = new Set(cptCodes).size === cptCodes.length;
        const canGroupAsMultiCpt =
            groupRemits.length > 1 && !hasNull && allUnique;
        if (canGroupAsMultiCpt) {
            tabs.push(groupRemits);
        } else {
            groupRemits.forEach((r) => tabs.push([r]));
        }
    });
    return tabs;
};

export const getRemitClaimsListNavigateUrl = (remitId: string) => {
    const remitClaimsFiltersFromLS = localStorage.getItem(
        "remitclaimslist_filters"
    );

    if (remitClaimsFiltersFromLS) {
        return `/remits/${remitId}/claims?${remitClaimsFiltersFromLS}`;
    }

    return `/remits/${remitId}/claims`;
};
